import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { CustomToastService } from 'src/app/core/services/custom-toast.service';
import { environment } from 'src/app/environments/environment';
import { SharedService } from 'src/app/shared/services/shared.service';

@Injectable()
export class InterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private currentUrl: string = '';

  // Centralize auth page routes
  private readonly AUTH_ROUTES: string[] = ['/shared/login', '/login', '/'];
  private readonly AUTH_ROUTE_PREFIXES: string[] = [
    '/shared/reset-password',
    '/shared/forget-password',
    '/shared/complete-account-setup',
  ];

  constructor(
    private router: Router,
    private http: HttpClient,
    private sharedService: SharedService,
    private toastService: CustomToastService,
    private cookie: CookieService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
      }
    });
  }

  // Check if we are currently on an auth-related page
  private isOnAuthPage(): boolean {
    const url = this.currentUrl.split('?')[0].split('#')[0];
    return (
      this.AUTH_ROUTES.includes(url) ||
      this.AUTH_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix))
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/assets/')) {
      return next.handle(req);
    }

    const token = this.cookie.get('accessToken');

    if (token) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isAuthPage = this.isOnAuthPage();

        if (error.status === 401) {
          return this.handle401Error(req, next, isAuthPage);
        } else if (error.status === 403) {
          this.sharedService.getErrorMessage(error);
          if (!isAuthPage) {
            this.toastService.error(
              'Access denied. You do not have permission to perform this action.',
            );
            this.router.navigate(['/home']);
          }
        } else if (error.status === 400) {
          const errorDetails = error.error?.errors || [];
          if (!isAuthPage) {
            if (Array.isArray(errorDetails) && errorDetails.length > 0) {
              const validationMessages = errorDetails
                .map((err: any) => `${err.field}: ${err.message}`)
                .join('\n');
              this.toastService.error(validationMessages);
            } else {
              const errorMessage =
                error.error?.message ||
                error.error?.title ||
                'Bad request. Please check your input.';
              this.toastService.error(errorMessage);
            }
          }
        } else if (error.status === 404 && !isAuthPage) {
          this.toastService.error('The requested resource was not found.');
        } else if (error.status === 500 && !isAuthPage) {
          this.toastService.error(
            'Server error occurred. Please try again later or contact support.',
          );
        } else if (!isAuthPage) {
          const errorMessage =
            error.error?.message || error.message || 'An unexpected error occurred.';
          this.toastService.error(errorMessage);
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
    isAuthPage: boolean,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.cookie.get('refreshToken');

      if (refreshToken) {
        const refreshUrl = `${environment.companyApiUrl}/auth/refresh`;

        return this.http.post<any>(refreshUrl, { refreshToken }).pipe(
          switchMap((response: any) => {
            this.isRefreshing = false;
            this.setAccessToken(response.accessToken);
            this.refreshTokenSubject.next(response.accessToken);

            req = req.clone({
              setHeaders: { Authorization: `Bearer ${response.accessToken}` },
            });

            return next.handle(req);
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.clearAuthCookies();

            if (!isAuthPage) {
              this.toastService.error('Your session has expired. Please log in again.');
              this.router.navigate(['shared/login']);
            }
            return throwError(() => err);
          }),
        );
      } else {
        if (!isAuthPage) {
          this.toastService.error('Authentication required. Please log in.');
          this.router.navigate(['shared/login']);
        }
      }
    }

    // If a refresh is already happening → wait until it's done and retry the request
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        req = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        });
        return next.handle(req);
      }),
    );
  }

  private setAccessToken(token: string) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      this.cookie.set('accessToken', token, undefined, '/');
    } else {
      this.cookie.set('accessToken', token, undefined, '/', `.${environment.domainName}`);
    }
  }

  private clearAuthCookies() {
    this.cookie.delete('accessToken', '/', `.${environment.domainName}`);
    this.cookie.delete('refreshToken', '/', `.${environment.domainName}`);
  }
}
